// Cross-platform health wire adapter (injuries, phases, supplements).
//
// Canonical wire form matches the deployed PWA:
//   injury: { id, joint, severity, date, notes, active, resolved }
//   phase:  { id, type, start, end, notes, active, startWeight?, targetWeight? }
//   supplement: { id, name, dosage, timing, takenDates }  (legacy: a bare name string)
//
// Readers accept Swift/legacy aliases (bodyPart/healed, startDate/endDate,
// Maintenance vs maintain, dose vs dosage) and extra history spellings.
// Writers emit the canonical keys so a deployed PWA can read iOS pushes.
// Missing activity/date flags stay unspecified — they are never invented as
// healthy or currently-active.
// Unknown extension fields (including nested objects) survive round-trip.
// Date-derived phase activity is not persisted as an explicit `active` override.
//
// This module is import-free so Node tests can load it directly.

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const SUPP_DAY_PREFIX = 'ft-supps-';
const SUPP_LIST_KEY = 'ft-supplements';

const PHASE_TYPES = {
  cut: 'cut',
  bulk: 'bulk',
  maintain: 'maintain',
  maintenance: 'maintain',
  strength: 'strength',
  deload: 'deload',
  recomp: 'recomp',
};

const INJURY_ALIASES = new Set([
  'id', 'joint', 'bodyPart', 'body_part', 'severity', 'date', 'notes',
  'active', 'healed', 'resolved', 'resolvedAt', 'healedAt', 'healed_at',
  'started', 'startedAt', 'created', 'name', 'area',
]);
const INJURY_CANON = new Set(['id', 'joint', 'severity', 'date', 'notes', 'active', 'resolved']);

const PHASE_ALIASES = new Set([
  'id', 'type', 'start', 'end', 'startDate', 'endDate', 'start_date', 'end_date',
  'notes', 'active', 'startWeight', 'targetWeight', 'start_weight', 'target_weight',
]);
const PHASE_CANON = new Set(['id', 'type', 'start', 'end', 'notes', 'active', 'startWeight', 'targetWeight']);

const SUPP_ALIASES = new Set([
  'id', 'name', 'label', 'title', 'dosage', 'dose', 'timing', 'when', 'time',
  'takenDates', 'taken_dates', 'taken', 'history', 'log', 'dates',
]);
const SUPP_CANON = new Set(['id', 'name', 'dosage', 'timing', 'takenDates']);

function todayISO() {
  const d = new Date();
  const z = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`;
}

function fallbackUid() {
  return 'h_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function str(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

function wireDay(v) {
  const s = str(v).trim();
  if (!s) return '';
  if (ISO_DAY.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10);
  return '';
}

function firstDay(obj, keys) {
  for (const k of keys) {
    const d = wireDay(obj?.[k]);
    if (d) return d;
  }
  return '';
}

function hasOwn(obj, key) {
  return isObj(obj) && Object.prototype.hasOwnProperty.call(obj, key);
}

function explicitBool(obj, key) {
  if (!isObj(obj) || !hasOwn(obj, key)) return undefined;
  const v = obj[key];
  if (typeof v === 'boolean') return v;
  if (v === 0 || v === '0' || v === 'false') return false;
  if (v === 1 || v === '1' || v === 'true') return true;
  return undefined;
}

function numOrZero(v) {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim()) {
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function slugId(prefix, name) {
  const slug = str(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return prefix + (slug || 'item');
}

function cloneJson(v) {
  if (v == null || typeof v !== 'object') return v;
  try { return JSON.parse(JSON.stringify(v)); } catch { return v; }
}

function pickExtras(raw, known) {
  if (!isObj(raw)) return {};
  const extra = {};
  for (const [k, v] of Object.entries(raw)) {
    if (known.has(k)) continue;
    extra[k] = cloneJson(v);
  }
  return extra;
}

function assignExtras(rec, extra) {
  if (!extra) return rec;
  for (const [k, v] of Object.entries(extra)) {
    if (Object.prototype.hasOwnProperty.call(rec, k)) continue;
    rec[k] = v;
  }
  return rec;
}

function copyCanonExtras(rec, canon) {
  return pickExtras(rec, canon);
}

// ── Injuries ──────────────────────────────────────────────────────────────

export function injuryJoint(inj) {
  if (!inj) return '';
  if (typeof inj === 'string') return inj;
  return str(inj.joint || inj.bodyPart || inj.body_part || inj.name || inj.area);
}

export function injuryIsActive(inj) {
  return isObj(inj) && inj.active === true;
}

export function injuryIsResolved(inj) {
  if (!isObj(inj)) return false;
  if (inj.active === true) return false;
  if (inj.active === false) return true;
  if (inj.healed === true) return true;
  if (inj.resolved) return true;
  return false;
}

export function normalizeInjuryFromWire(raw, opts = {}) {
  const uid = opts.uid || fallbackUid;
  if (raw == null || raw === false) return null;
  if (typeof raw === 'string') {
    const joint = raw.trim();
    if (!joint) return null;
    // A bare label has no activity flag — do not invent active or resolved.
    const rec = { id: uid(), joint, severity: '', date: '', notes: '', resolved: null };
    return rec;
  }
  if (!isObj(raw)) return null;

  const id = str(raw.id) || uid();
  const joint = injuryJoint(raw);
  const severity = str(raw.severity).toLowerCase();
  const date = firstDay(raw, ['date', 'started', 'startedAt', 'created']);
  const notes = str(raw.notes);
  const resolved = firstDay(raw, ['resolved', 'resolvedAt', 'healedAt', 'healed_at']);

  const rec = { id, joint, severity, date, notes, resolved: resolved || null };

  const active = explicitBool(raw, 'active');
  const healed = explicitBool(raw, 'healed');
  if (active === true) {
    rec.active = true;
    rec.resolved = null;
  } else if (active === false) {
    rec.active = false;
    if (!rec.resolved && healed !== false) rec.resolved = resolved || null;
  } else if (healed === true) {
    rec.active = false;
    rec.resolved = rec.resolved || null;
  } else if (healed === false) {
    rec.active = true;
    rec.resolved = null;
  } else if (resolved) {
    rec.active = false;
    rec.resolved = resolved;
  }
  // else: omit `active` so callers cannot treat the row as healthy or current.

  return assignExtras(rec, pickExtras(raw, INJURY_ALIASES));
}

export function normalizeInjuriesFromWire(list, opts = {}) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of list) {
    const rec = normalizeInjuryFromWire(raw, opts);
    if (!rec) continue;
    if (seen.has(rec.id)) continue;
    seen.add(rec.id);
    out.push(rec);
  }
  return out;
}

export function serializeInjuriesForWire(list, opts = {}) {
  return normalizeInjuriesFromWire(list, opts).map((inj) => {
    const rec = {
      id: inj.id,
      joint: inj.joint,
      severity: inj.severity || '',
      date: inj.date || '',
      notes: inj.notes || '',
      resolved: inj.resolved || null,
    };
    if (inj.active === true || inj.active === false) rec.active = inj.active;
    return assignExtras(rec, copyCanonExtras(inj, INJURY_CANON));
  });
}

// ── Phases ────────────────────────────────────────────────────────────────

export function phaseTypeId(type) {
  const raw = str(type).trim();
  if (!raw) return '';
  const key = raw.toLowerCase().replace(/\s+/g, '');
  return PHASE_TYPES[key] || raw.toLowerCase();
}

export function phaseStart(phase) {
  if (!isObj(phase)) return '';
  return firstDay(phase, ['start', 'startDate', 'start_date']) || str(phase.start || phase.startDate || phase.start_date);
}

export function phaseEnd(phase) {
  if (!isObj(phase)) return '';
  return firstDay(phase, ['end', 'endDate', 'end_date']) || str(phase.end || phase.endDate || phase.end_date);
}

export function phaseIsActive(phase, todayStr) {
  if (!isObj(phase)) return false;
  if (phase.active === true) return true;
  if (phase.active === false) return false;
  const today = todayStr || todayISO();
  const start = phaseStart(phase);
  if (!start) return false;
  if (start > today) return false;
  const end = phaseEnd(phase);
  if (!end) return true;
  return today <= end;
}

export function normalizePhaseFromWire(raw, opts = {}) {
  const uid = opts.uid || fallbackUid;
  if (raw == null || typeof raw === 'string' || !isObj(raw)) return null;

  const id = str(raw.id) || uid();
  const type = phaseTypeId(raw.type);
  const start = phaseStart(raw);
  const end = phaseEnd(raw) || null;
  const notes = str(raw.notes);
  const rec = { id, type, start, end: end || null, notes };

  if (hasOwn(raw, 'startWeight') || hasOwn(raw, 'start_weight')) {
    rec.startWeight = numOrZero(raw.startWeight ?? raw.start_weight);
  }
  if (hasOwn(raw, 'targetWeight') || hasOwn(raw, 'target_weight')) {
    rec.targetWeight = numOrZero(raw.targetWeight ?? raw.target_weight);
  }

  // Only an explicit `active` flag is stored. Date-derived current/ended/future
  // state is computed by phaseIsActive so progression stays dynamic after a round-trip.
  const active = explicitBool(raw, 'active');
  if (active === true || active === false) rec.active = active;

  return assignExtras(rec, pickExtras(raw, PHASE_ALIASES));
}

export function normalizePhasesFromWire(list, opts = {}) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of list) {
    const rec = normalizePhaseFromWire(raw, opts);
    if (!rec) continue;
    if (seen.has(rec.id)) continue;
    seen.add(rec.id);
    out.push(rec);
  }
  return out;
}

export function serializePhasesForWire(list, opts = {}) {
  return normalizePhasesFromWire(list, opts).map((p) => {
    const rec = {
      id: p.id,
      type: phaseTypeId(p.type),
      start: p.start || '',
      end: p.end || null,
      notes: p.notes || '',
    };
    if (p.active === true || p.active === false) rec.active = p.active;
    if (p.startWeight != null) rec.startWeight = p.startWeight;
    if (p.targetWeight != null) rec.targetWeight = p.targetWeight;
    return assignExtras(rec, copyCanonExtras(p, PHASE_CANON));
  });
}

// ── Supplements ───────────────────────────────────────────────────────────

function isTakenFalse(val) {
  return val === false || val === 0 || val === '0' || val === 'false';
}

function isTakenTrue(val) {
  return val === true || val === 1 || val === '1' || val === 'true';
}

function cleanDateList(v) {
  const out = [];
  const seen = new Set();
  const push = (d) => {
    const day = wireDay(d);
    if (!day || seen.has(day)) return;
    seen.add(day);
    out.push(day);
  };
  if (Array.isArray(v)) {
    for (const item of v) {
      if (typeof item === 'string' || typeof item === 'number') push(item);
      else if (isObj(item)) {
        if (isTakenFalse(item.taken)) continue;
        push(item.date || item.day || item.on);
      }
    }
  } else if (isObj(v)) {
    for (const [k, val] of Object.entries(v)) {
      if (isTakenFalse(val)) continue;
      if (isTakenTrue(val) || val == null || isObj(val) || str(val)) push(k);
    }
  }
  out.sort();
  return out;
}

export function supplementName(raw) {
  if (typeof raw === 'string') return raw.trim();
  if (!isObj(raw)) return '';
  return str(raw.name || raw.label || raw.title).trim();
}

export function normalizeSupplementFromWire(raw, opts = {}) {
  const uid = opts.uid || fallbackUid;
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const name = raw.trim();
    if (!name) return null;
    return { id: slugId('supp_', name), name, dosage: '', timing: '', takenDates: [] };
  }
  if (!isObj(raw)) return null;
  const name = supplementName(raw);
  if (!name) return null;
  const dosage = str(raw.dosage || raw.dose);
  const timing = str(raw.timing || raw.when || raw.time);
  const takenDates = cleanDateList(
    raw.takenDates || raw.taken_dates || raw.taken || raw.history || raw.log || raw.dates
  );
  const id = str(raw.id) || slugId('supp_', name);
  const rec = { id, name, dosage, timing, takenDates };
  return assignExtras(rec, pickExtras(raw, SUPP_ALIASES));
}

export function normalizeSupplementsFromWire(list, opts = {}) {
  if (!Array.isArray(list)) return [];
  const out = [];
  const seen = new Set();
  for (const raw of list) {
    const rec = normalizeSupplementFromWire(raw, opts);
    if (!rec) continue;
    if (seen.has(rec.id)) continue;
    seen.add(rec.id);
    out.push(rec);
  }
  return out;
}

export function historyFromSupplements(list) {
  const days = {};
  for (const s of normalizeSupplementsFromWire(list)) {
    for (const day of s.takenDates || []) {
      if (!days[day]) days[day] = {};
      days[day][s.name] = true;
    }
  }
  return days;
}

export function mergeSupplementHistory(list, dailyMaps) {
  const supps = normalizeSupplementsFromWire(list);
  if (!dailyMaps || typeof dailyMaps !== 'object') return supps;
  const byName = new Map(supps.map((s) => [s.name, s]));
  for (const [day, names] of Object.entries(dailyMaps)) {
    const date = wireDay(day);
    if (!date || !isObj(names)) continue;
    for (const [name, taken] of Object.entries(names)) {
      const key = str(name).trim();
      if (!key) continue;
      const rec = byName.get(key);
      // Historical ft-supps-* maps must not resurrect members removed from the list.
      if (!rec) continue;
      if (isTakenFalse(taken)) {
        rec.takenDates = (rec.takenDates || []).filter((d) => d !== date);
        continue;
      }
      if (isTakenTrue(taken)) {
        if (!rec.takenDates.includes(date)) rec.takenDates.push(date);
      }
    }
  }
  for (const s of supps) s.takenDates.sort();
  return supps;
}

function lsGet(storage, key) {
  if (storage && typeof storage.get === 'function') return storage.get(key);
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
}

function lsSet(storage, key, value) {
  if (storage && typeof storage.set === 'function') return storage.set(key, value);
  try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; }
}

function lsDel(storage, key) {
  if (storage && typeof storage.del === 'function') return storage.del(key);
  if (storage && typeof storage.remove === 'function') return storage.remove(key);
  try { localStorage.removeItem(key); return true; } catch { return false; }
}

export function collectLocalSupplementHistory(storage) {
  const out = {};
  const put = (key, value) => {
    if (!key || !key.startsWith(SUPP_DAY_PREFIX) || key === 'ft-supps-logged') return;
    const date = key.slice(SUPP_DAY_PREFIX.length);
    if (!ISO_DAY.test(date)) return;
    const map = typeof value === 'string' ? safeParse(value) : value;
    if (isObj(map)) out[date] = map;
  };
  if (storage && typeof storage.each === 'function') {
    storage.each(put);
    return out;
  }
  try {
    if (typeof localStorage !== 'undefined' && localStorage.length >= 0) {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k) put(k, lsGet(storage, k));
      }
    }
  } catch { /* node / private mode */ }
  return out;
}

function safeParse(v) {
  try { return JSON.parse(v); } catch { return v; }
}

export function applySupplementHistoryLocal(list, storage) {
  const days = historyFromSupplements(list);
  const existing = collectLocalSupplementHistory(storage);
  const allDates = new Set([...Object.keys(existing), ...Object.keys(days)]);
  // Canonical takenDates replace daily maps; they do not union with leftover local flags.
  for (const date of allDates) {
    const key = SUPP_DAY_PREFIX + date;
    const next = days[date];
    if (next && Object.keys(next).length) lsSet(storage, key, next);
    else lsDel(storage, key);
  }
  return days;
}

export function serializeSupplementsForWire(list, dailyMaps, opts = {}) {
  return mergeSupplementHistory(list, dailyMaps).map((s) => {
    const rec = {
      id: s.id,
      name: s.name,
      dosage: s.dosage || '',
      timing: s.timing || '',
      takenDates: [...(s.takenDates || [])],
    };
    return assignExtras(rec, copyCanonExtras(s, SUPP_CANON));
  });
}

// ── Bundle helpers for sync / restore ─────────────────────────────────────

export function applyHealthFromWire(data, opts = {}) {
  if (!isObj(data)) return data;
  const today = opts.today || todayISO();
  const uid = opts.uid || fallbackUid;
  if (Array.isArray(data.injuries)) data.injuries = normalizeInjuriesFromWire(data.injuries, { uid, today });
  if (Array.isArray(data.phases)) data.phases = normalizePhasesFromWire(data.phases, { uid, today });
  if (Array.isArray(data.supplements)) {
    const rawSupplements = data.supplements;
    data.supplements = normalizeSupplementsFromWire(rawSupplements, { uid, today });
    if (opts.applyLocalHistory !== false && opts.migrateLegacyHistory === true) {
      const historyKeys = ['takenDates', 'taken_dates', 'taken', 'history', 'log', 'dates'];
      const legacyNames = new Set();
      const canonicalNames = new Set();
      for (const raw of rawSupplements) {
        const name = normalizeSupplementsFromWire([raw], { uid })[0]?.name;
        if (!name) continue;
        (historyKeys.some(key => hasOwn(raw, key)) ? canonicalNames : legacyNames).add(name);
      }
      for (const name of canonicalNames) legacyNames.delete(name);
      const localDays = collectLocalSupplementHistory(opts.storage);
      const legacyDays = Object.fromEntries(Object.entries(localDays).map(([day, names]) =>
        [day, Object.fromEntries(Object.entries(names).filter(([name]) => legacyNames.has(name)))]));
      data.supplements = mergeSupplementHistory(data.supplements, legacyDays);
    }
    if (opts.applyLocalHistory !== false) {
      applySupplementHistoryLocal(data.supplements, opts.storage);
      if (opts.persistList !== false) lsSet(opts.storage, SUPP_LIST_KEY, data.supplements);
    }
  }
  return data;
}

export function healthSettingsForWire(state, opts = {}) {
  const today = opts.today || todayISO();
  const uid = opts.uid || fallbackUid;
  const daily = opts.dailyMaps || collectLocalSupplementHistory(opts.storage);
  const localSupps = opts.supplements != null
    ? opts.supplements
    : (state && state.supplements) || lsGet(opts.storage, SUPP_LIST_KEY) || [];
  return {
    phases: serializePhasesForWire(state?.phases, { uid, today }),
    injuries: serializeInjuriesForWire(state?.injuries, { uid, today }),
    supplements: serializeSupplementsForWire(localSupps, daily, { uid, today }),
  };
}

/** Normalize a backup/import payload so native aliases do not land in PWA state. */
export function normalizeBackupHealth(data, opts = {}) {
  if (!isObj(data)) return data;
  return applyHealthFromWire(data, { applyLocalHistory: false, persistList: false, ...opts });
}

/** Hydrate local LS snapshots for INIT so Home/AI/analytics see canonical fields. */
export function hydrateLocalHealth(snapshot, opts = {}) {
  const src = isObj(snapshot) ? snapshot : {};
  return {
    phases: normalizePhasesFromWire(src['ft-phases'] || src.phases || [], opts),
    injuries: normalizeInjuriesFromWire(src['ft-injuries'] || src.injuries || [], opts),
  };
}

export const HEALTH_WIRE = {
  injury: ['id', 'joint', 'severity', 'date', 'notes', 'active', 'resolved'],
  phase: ['id', 'type', 'start', 'end', 'notes', 'active'],
  supplement: ['id', 'name', 'dosage', 'timing', 'takenDates'],
};
